import { useState } from "react";
import { Headphones, X, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useRecaptcha } from "../hooks/useRecaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportButton() {
  const { executeRecaptcha } = useRecaptcha();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    prioriteit: "Normaal",
    beschrijving: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = await executeRecaptcha('support');
      
      await axios.post(`${API}/support`, {
        ...formData,
        recaptcha_token: token || '',
      });
      toast.success("Support aanvraag verzonden! We nemen zo snel mogelijk contact met u op.");
      setFormData({
        naam: "",
        email: "",
        telefoon: "",
        prioriteit: "Normaal",
        beschrijving: "",
      });
      setIsOpen(false);
    } catch (error) {
      if (error.message && error.message.includes('reCAPTCHA')) {
        toast.error('reCAPTCHA verificatie mislukt. Probeer het opnieuw.');
      } else {
        toast.error("Er is iets misgegaan. Probeer het opnieuw.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating Support Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
        data-testid="floating-support-btn"
      >
        <Headphones className="w-7 h-7" />
      </button>

      {/* Support Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="support-modal">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-2xl animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Headphones className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Support Aanvraag</h2>
                  <p className="text-sm text-slate-500">Wij helpen u graag verder</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="close-support-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="support-naam" className="text-slate-700">Naam *</Label>
                  <Input
                    id="support-naam"
                    value={formData.naam}
                    onChange={(e) => setFormData({ ...formData, naam: e.target.value })}
                    required
                    className="bg-white border-slate-300 text-slate-900"
                    placeholder="Uw naam"
                    data-testid="support-input-naam"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-email" className="text-slate-700">Email *</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-white border-slate-300 text-slate-900"
                    placeholder="uw@email.be"
                    data-testid="support-input-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="support-telefoon" className="text-slate-700">Telefoon</Label>
                  <Input
                    id="support-telefoon"
                    value={formData.telefoon}
                    onChange={(e) => setFormData({ ...formData, telefoon: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900"
                    placeholder="+32 ..."
                    data-testid="support-input-telefoon"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-prioriteit" className="text-slate-700">Prioriteit</Label>
                  <Select
                    value={formData.prioriteit}
                    onValueChange={(value) => setFormData({ ...formData, prioriteit: value })}
                  >
                    <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="support-select-prioriteit">
                      <SelectValue placeholder="Selecteer" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="Laag">Laag</SelectItem>
                      <SelectItem value="Normaal">Normaal</SelectItem>
                      <SelectItem value="Hoog">Hoog</SelectItem>
                      <SelectItem value="Kritiek">Kritiek</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-beschrijving" className="text-slate-700">Beschrijving *</Label>
                <Textarea
                  id="support-beschrijving"
                  value={formData.beschrijving}
                  onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                  required
                  rows={4}
                  className="bg-white border-slate-300 text-slate-900 resize-none"
                  placeholder="Beschrijf uw probleem of vraag..."
                  data-testid="support-input-beschrijving"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-accent"
                data-testid="support-submit-btn"
              >
                {isSubmitting ? (
                  "Verzenden..."
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Verstuur Aanvraag
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
